import {
  BlockVariant,
  ButtonBlockProps,
  CommentBlockProps,
  DynamicBlock,
  DynamicBlocks,
  InputTextareaBlockProps,
  TextBlockProps,
} from '@/components/block-builder/block-builder.types';
import { FileService } from '@/components/shared/file-list/file-list.service';
import { PpmDetails } from '@/types';
import { inject, Injectable } from '@angular/core';
import _ from 'lodash';
import { BehaviorSubject, map, Observable } from 'rxjs';

interface getPreDraftPpmBlocksProps {
  save?: () => void;
  rest?: () => void;
  loading?: BehaviorSubject<boolean>;
}

interface getInspectPpmBlocksProps {
  refreshCount: number;
  status?: string;
  version?: number;
  accessHistory?: () => void;
  downloadSigned?: () => void;
  downloadExcelModel?: () => void;
  refresh?: () => void;
}

interface getMeetingPpmBlocksProps {
  refreshCount: number;
  status?: string;
  version?: number;
  accessHistory?: () => void;
  downloadSigned?: () => void;
}

interface getDraftPpmBlocksProps {
  refreshCount: number;
  isAltered?: Observable<boolean>;
  save?: () => void;
  rest?: () => void;
  validate?: () => void;
  remove?: () => void;
  accessHistory?: () => void;
}

interface getFlowablePpmBlocksProps {
  refreshCount: number;
  task?: PpmDetails;

  save?: () => void;
  rest?: () => void;
  remove?: () => void;
  validate?: (value?: string) => void;
  isAltered?: Observable<boolean>;
  accessHistory?: () => void;
  //ac validation **********************
  openDownloadDialog?: () => void;
  openUploadDialog?: () => void;
  // **********************************
  reject?: () => void;
  comment?: BehaviorSubject<string>;
  onCommentChange?: (value: string) => void;
  error?: BehaviorSubject<string | null>;
}

export enum TaskVariant {
  VALIDATION = 'Validation (AC)',
  PUBLICATION = '(AC) Publication',
  RECTIFICATION = 'Rectification (AC)',
  VALIDATION_CPMP = '(CPMP)Validation DAC',
  RESERVED = 'Réserve (CPMP)',
  AVIS_SOUS_COMMISSION_SPECIAL = '(CNCMP) Avis sous-commission spécialisée',
  AFFECTATION_COMMISSION_PERMANENTE = '(CNCMP) commission permanente Affectation',
  DECISION_COMMISSION_PERMANENTE = '(CNCMP) Décision commission permanente',
  REVISION_COMMISSION_SPECIAL = '(CNCMP) Révision sous-commission spécialisée',
  FILTRAGE_ET_QUALIFICATION_DOSSIER = '(CPMP) Filtrage et qualification dossier',
  AFFECTATION_COMMISSION_SPECIALISEE = '(CNCMP) commission spécialisée Affectation',
  AVIS_NOTE_RAPPORTEUR_COMMISSION_SPECIALISEE = '(CNCMP) Avis + Note Rapporteur commission spécialisée',
  FILTRAGE_ET_QUALIFICATION_DOSSIER_CNCMP = '(CNCMP) Filtrage et qualification dossier',
}

@Injectable({
  providedIn: 'root',
})
export class PpmWorkflowBlocksService {
  fileService = inject(FileService);
  static mapToFrenchValues(value: string) {
    if (value == 'Validate') return 'Valider';
    if (value == 'Reject') return 'Rejeter';
    if (value == 'Publish') return 'Publier';
    if (value == 'Assign') return 'Affecter';
    return value;
  }

  accessHistoryButton = (accessHistory?: () => void): DynamicBlock => {
    return {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'ppm.actions.access_history',
        onClick: accessHistory,
        severity: 'secondary',
      },
    };
  };

  refreshedLabel = (refreshCount: number): DynamicBlock<TextBlockProps> => {
    if (refreshCount === 0)
      return {
        variant: BlockVariant.CUSTOM,
      };
    else
      return {
        variant: BlockVariant.TEXT,
        props: {
          blocks: [
            {
              content: refreshCount === 1 ? 'Ce document a été actualisé une seule fois' : `Ce document a été actualisé ${refreshCount} fois`,
              class: 'text-[1rem] font-thin opacity-70 no-select italic',
            },
          ],
          orientation: 'horizontal',
          gap: 1,
        },
        class: 'w-fit mx-auto',
      };
  };

  getInspectPpmBlocks = ({
    refreshCount,
    status,
    version,
    accessHistory,
    downloadSigned,
    downloadExcelModel,
    refresh,
  }: getInspectPpmBlocksProps): DynamicBlocks[] => {
    const finalBlocks: DynamicBlocks[] = [];

    const refreshButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'ppm.actions.refresh',
        onClick: () => refresh?.(),
      },
    };

    const downloadSignedButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'ppm.actions.download_model',
        onClick: () => downloadSigned?.(),
        severity: 'info',
      },
    };

    const downloadExcelModelButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'ppm.actions.download_model_excel',
        onClick: () => downloadExcelModel?.(),
        severity: 'info',
      },
    };

    finalBlocks.push({});
    if (status === 'PUBLIE') {
      finalBlocks[0]['0'] = [refreshButton];
    }
    if (downloadSigned) finalBlocks[0]['1'] = [downloadSignedButton];
    if (downloadExcelModel) finalBlocks[0]['2'] = [downloadExcelModelButton];

    if (_.isEqual(finalBlocks[0], {})) finalBlocks.splice(0, 1);

    finalBlocks.push({
      '0': [this.accessHistoryButton(accessHistory)],
      '1': [this.refreshedLabel(refreshCount)],
    });

    return finalBlocks;
  };

  getMeetingPpmBlocks = ({ refreshCount, accessHistory, downloadSigned, status, version }: getMeetingPpmBlocksProps): DynamicBlocks[] => {
    const inspectBlocks = this.getInspectPpmBlocks({ refreshCount, accessHistory, downloadSigned, status, version });

    if (inspectBlocks[0]) {
      delete inspectBlocks[0]['0'];
    }

    return inspectBlocks;
  };

  getPreDraftPpmBlocks = ({ save, rest, loading }: getPreDraftPpmBlocksProps): DynamicBlocks => {
    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Enregistrer',
        onClick: () => save?.(),
        loading: loading,
      },
    };
    const resetButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Réinitialiser',
        onClick: rest,
        severity: 'secondary',
      },
    };
    return {
      '1': [saveButton],
      '2': [resetButton],
    };
  };

  getDraftPpmBlocks = ({ refreshCount, isAltered, save, rest, validate, accessHistory }: getDraftPpmBlocksProps): DynamicBlocks[] => {
    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Enregistrer',
        onClick: () => save?.(),
        disabled: isAltered?.pipe(map(value => !value)),
      },
    };
    const startWorkflowButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Passer pour validation',
        onClick: () => validate?.(),
      },
    };

    return [
      {
        '0': [saveButton, startWorkflowButton], //Enregistrer + Passer pour validation
        // '2': blocks['2'], // reset
        // '3': [deleteButton],
      },
      {
        '0': [this.accessHistoryButton(accessHistory)],
        '1': [this.refreshedLabel(refreshCount)],
      },
    ];
  };

  getFlowablePpmBlocks = ({
    refreshCount,
    task,
    validate,
    isAltered,
    openDownloadDialog,
    openUploadDialog,
    reject,
    comment,
    onCommentChange,
    error,
    accessHistory,
  }: getFlowablePpmBlocksProps): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [];
    let renderTextarea = false;

    //detect status
    if (task?.taskName == TaskVariant.VALIDATION) {
      //detect actions
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          if (`${step.validationValue}` == 'true') {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Télécharger`,
                severity: 'success',
                onClick: openDownloadDialog,
              },
            });
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Déposer`,
                severity: 'secondary',
                onClick: openUploadDialog,
              },
            });
          } else {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
                onClick: () => reject?.(),
              },
            });
          }
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.PUBLICATION) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: 'Publier',
          onClick: () => validate?.('true'),
        },
      });
    } else if (task?.taskName == TaskVariant.RECTIFICATION) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: 'Enregistrer',
              onClick: () => validate?.(),
            },
          });
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => validate?.('true'),
              disabled: isAltered,
            },
          });
        });
    } else if (task?.taskName == TaskVariant.RESERVED) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AVIS_SOUS_COMMISSION_SPECIAL) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AFFECTATION_COMMISSION_PERMANENTE) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: PpmWorkflowBlocksService.mapToFrenchValues('Assign'),
          onClick: () => validate?.('true'),
        },
      });
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: PpmWorkflowBlocksService.mapToFrenchValues('Reject'),
          onClick: () => reject?.(),
        },
      });

      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.DECISION_COMMISSION_PERMANENTE) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.REVISION_COMMISSION_SPECIAL) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.FILTRAGE_ET_QUALIFICATION_DOSSIER) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AFFECTATION_COMMISSION_SPECIALISEE) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: PpmWorkflowBlocksService.mapToFrenchValues('Assign'),
          onClick: () => validate?.('true'),
        },
      });
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: PpmWorkflowBlocksService.mapToFrenchValues('Reject'),
          onClick: () => reject?.(),
        },
      });

      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AVIS_NOTE_RAPPORTEUR_COMMISSION_SPECIALISEE) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
    } else if (task?.taskName == TaskVariant.FILTRAGE_ET_QUALIFICATION_DOSSIER_CNCMP) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PpmWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    }

    const textareablock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'Commentaire',
        rows: 5,
        value: comment?.getValue(),
        placeholder: 'Saisissez un commentaire',
        onChange: (value: string) => onCommentChange?.(value),
        error: error,
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: task?.traceability?.comment || '',
        date: task?.traceability?.dateComment || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const meetingRequiredBlock: DynamicBlock<TextBlockProps> = {
      variant: BlockVariant.TEXT,
      props: {
        gap: 1,
        blocks: [
          {
            content: 'Ce dossier ne peut pas être traité en dehors d’une réunion, il est nécessaire d’en planifier une.',
            class: 'text-sm italic opacity-70',
          },
        ],
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (task?.traceability?.comment) finalBlocks['0'] = [commentBlock];
    if (blocks.length > 0 && !task?.meetingRequired) finalBlocks['1'] = blocks;
    if (renderTextarea && !task?.meetingRequired) finalBlocks['2'] = [textareablock];
    if (task?.meetingRequired) finalBlocks['3'] = [meetingRequiredBlock];
    return [
      ...(Object.keys(finalBlocks).length ? [finalBlocks] : []),
      {
        '0': [this.accessHistoryButton(accessHistory)],
        '1': [this.refreshedLabel(refreshCount)],
      },
    ];
  };
}
